import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getMyBroadcasts,
  getMyNotices,
  type ReaderBroadcast,
  type ReaderNotice,
} from "../../shared/api/platform";

/**
 * The bell — one place a signed-in adult can find what was said to them.
 *
 * 🔴 IT READS THE TWO FEEDS THAT ALREADY EXIST AND INVENTS NEITHER.
 * `GET /notices/mine` and `GET /broadcasts/mine` are both already role-scoped
 * **server side, from the token** — a `?role=` on either would hand a parent
 * whatever an operator wrote for teachers. So one component serves the parent,
 * the teacher and the operator without ever asking who is reading: the answer
 * comes back different because the credential is different.
 *
 * 🔴 IT DOES NOT REPLACE `PlatformBroadcasts`, AND THE TWO ANSWER DIFFERENT
 * QUESTIONS. That strip is what you cannot miss — it sits in the content column
 * on arrival and is dismissed once read. This is where you go to look something
 * up again afterwards, which is exactly what dismissing the strip used to make
 * impossible. Dismissing there does not mark anything read here; the two keep
 * separate state on purpose, because "I have cleared this off my screen" and "I
 * have read my notifications" are different claims.
 *
 * ⚠️ READ STATE IS `localStorage`, AND THAT IS THE SAME TRADE THE STRIP MADE.
 * `platform.prisma` records the reason in the schema itself — *"there is no
 * `dismissedBy` table"* — and it holds here for the same cost: a row per user
 * per notification is a table that grows with every account, records a reading
 * habit, and answers a question nobody asks. The price is that clearing a
 * browser makes the badge come back, which is the smaller failure.
 *
 * ⚠️ THE KEY CARRIES EACH ID, never a single `seen: true` flag — otherwise the
 * next notification arrives pre-read and the bell silently stops working.
 */
const SEEN_KEY = "kalqy.notifications.seen";

/**
 * 🔴 CAPPED, BECAUSE THIS LIST ONLY EVER GROWS. `localStorage` has a ~5 MB
 * budget and an uncapped key is a slow leak. Both feeds return at most three
 * rows each, so fifty ids is far more history than can ever be live at once.
 */
const SEEN_CAP = 50;

function readSeen(): Set<string> {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [],
    );
  } catch {
    /* A corrupt or unavailable store must not hide the bell, and a throw here
       would take the whole shell down on a browser in private mode. Showing
       everything as unread is the safe direction to fail. */
    return new Set();
  }
}

function rememberSeen(ids: readonly string[]): void {
  try {
    const next = [...readSeen(), ...ids];
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(next.slice(-SEEN_CAP)));
  } catch {
    /* Private mode. The badge returns next load, which is the harmless half. */
  }
}

/**
 * One row, whichever feed it came from.
 *
 * ⚠️ THE ATTRIBUTION IS KEPT RATHER THAN FLATTENED AWAY. A release notice and
 * an operator's broadcast are different things to a reader — one is "the
 * product changed", the other is "a person is telling you something" — and the
 * line under each says which. Merging them into an undifferentiated list would
 * make the bell a pile.
 */
interface Item {
  id: string;
  title: string;
  body: string;
  publishedAt: string;
  /** Who is speaking: an operator's label, or the version a notice describes. */
  attribution: string;
}

const COPY = {
  /** ⚠️ The accessible name. Never just "Bell" — it names the thing, not the glyph. */
  open: "Notifications",
  heading: "Notifications",
  empty: "Nothing new. Messages from your school and from Kalqy appear here.",
  /** Read by a screen reader; the visible badge is a number. */
  unreadSuffix: "unread",
} as const;

/**
 * The bell glyph.
 *
 * 🔴 DRAWN HERE RATHER THAN ADDED TO `shared/ui/icons.tsx`, AND THAT IS NOT
 * LAZINESS. That file is generated — *"GENERATED FILE — DO NOT EDIT BY HAND"* —
 * from the delivered Foundations design by `scripts/generate-icons.mjs`, and
 * `npm run icons:check` fails CI when it drifts. The delivered set is twenty
 * icons and contains no bell, so hand-adding one there would either be reverted
 * by the next regenerate or break the check.
 *
 * ⚠️ IT STILL OBEYS THE STROKE SYSTEM: 24px grid, 2px round-cap strokes,
 * `currentColor`, `aria-hidden`. §6's rule is one stroke set, and this matches
 * it exactly rather than introducing a second look.
 *
 * 📋 If a bell is added to Foundations §04, delete this and import `BellIcon`.
 */
function BellGlyph({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8a6 6 0 0 0-12 0c0 6-2 7-2 7h16s-2-1-2-7Z" />
      <path d="M13.7 19a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

export interface NotificationBellProps {
  /**
   * The signed-in reader's bearer token.
   *
   * ⚠️ NO TOKEN, NO BELL. Both feeds decide what to return from the token and
   * from nowhere else, so a signed-out shell must not draw the control at all —
   * a bell that always reads empty teaches people to stop looking at it.
   */
  token?: string;
  /**
   * How the trigger is drawn. The behaviour behind it never changes.
   *
   * `toolbar` is the shell's bare glyph. `pill` is the bordered card the parent
   * overview's own header uses, so the bell sits beside the child switcher as
   * one row of matching controls rather than as a button from another screen.
   *
   * 🔴 A VARIANT RATHER THAN A SECOND COMPONENT. The fetch, the single seen-list
   * and the unread arithmetic are the parts worth getting right once; two
   * components would be two badges that could disagree about the same feed.
   */
  variant?: "toolbar" | "pill";
}

export function NotificationBell({ token, variant = "toolbar" }: NotificationBellProps) {
  const [notices, setNotices] = useState<ReaderNotice[]>([]);
  const [broadcasts, setBroadcasts] = useState<ReaderBroadcast[]>([]);
  const [seen, setSeen] = useState<Set<string>>(() => readSeen());
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (token === undefined) return;
    const controller = new AbortController();
    /*
     * 🔴 FAILURE IS SILENCE, `PlatformBroadcasts`' RULE AND ITS REASON. This
     * control sits on every screen in the product; a bell rendering "couldn't
     * load" above a teacher's class list would be worse than one rendering
     * nothing, and nothing else on the page depends on it.
     *
     * ⚠️ THE TWO CALLS ARE INDEPENDENT, so one feed being down still shows the
     * other. `Promise.all` here would let a broken notices route hide every
     * broadcast an operator sent.
     */
    getMyNotices({ token, signal: controller.signal })
      .then(setNotices)
      .catch(() => {
        setNotices([]);
      });
    getMyBroadcasts({ token, signal: controller.signal })
      .then(setBroadcasts)
      .catch(() => {
        setBroadcasts([]);
      });
    return () => {
      controller.abort();
    };
  }, [token]);

  const items = useMemo<Item[]>(() => {
    const merged: Item[] = [
      ...broadcasts.map((b) => ({
        id: b.id,
        title: b.title,
        body: b.body,
        publishedAt: b.publishedAt,
        attribution: b.authorLabel,
      })),
      ...notices.map((n) => ({
        id: n.id,
        title: n.title,
        body: n.body,
        publishedAt: n.publishedAt,
        attribution: `Kalqy ${n.version}`,
      })),
    ];
    /* Newest first. An unparseable date sorts rather than throwing — the server
       sends ISO-8601, and a row we cannot date is still a row worth showing. */
    return merged.sort((a, b) => (Date.parse(b.publishedAt) || 0) - (Date.parse(a.publishedAt) || 0));
  }, [broadcasts, notices]);

  const unread = useMemo(() => items.filter((i) => !seen.has(i.id)), [items, seen]);

  /*
   * ⚠️ OPENING IS WHAT MARKS THEM READ, AND ONLY OPENING. Marking on arrival
   * would clear the badge for somebody who never looked, which is the one thing
   * a notification count must not do.
   */
  const toggle = useCallback(() => {
    setOpen((wasOpen) => {
      if (wasOpen) return false;
      const ids = items.map((i) => i.id);
      rememberSeen(ids);
      setSeen((current) => new Set([...current, ...ids]));
      return true;
    });
  }, [items]);

  /* Escape closes, and a click anywhere outside closes — the two exits people
     try before looking for a close button. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  if (token === undefined) return null;

  const count = unread.length;
  const pill = variant === "pill";

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        /* ⚠️ THE COUNT IS IN THE ACCESSIBLE NAME, because the badge is a visual
           number and a screen reader hears the label, not the pixel. */
        aria-label={count > 0 ? `${COPY.open}, ${String(count)} ${COPY.unreadSuffix}` : COPY.open}
        className={
          pill
            ? "relative cursor-pointer rounded-2xl border border-line bg-surface p-2.5 text-ink shadow-2xs transition-colors hover:bg-canvas"
            /* ⚠️ 44px UNDER A THUMB, 36px UNDER A MOUSE. This is the only control
               in the header besides the drawer button, it sits in the top-right
               corner — the furthest point on a phone from where a thumb rests —
               and it was 36px square at every size. `pointer-fine:` rather than
               `sm:` so a phone held sideways keeps the larger target; see
               `DashPrimitives`' `VARIANT` for the full reasoning. */
            : "relative inline-flex h-[44px] w-[44px] pointer-fine:h-9 pointer-fine:w-9 items-center justify-center rounded-lg text-ink-soft transition-colors duration-150 ease-dash hover:bg-canvas hover:text-ink"
        }
      >
        {/*
         * ✅ ONE BELL, BOTH VARIANTS. The pill drew a 🔔 emoji here, with a note
         * conceding that `icons.tsx` — *"EMOJI ARE NOT ICONS — they identify the
         * games and nothing else"* — says it should be `BellGlyph`, and keeping
         * it only because a neighbouring control had the same problem. Two wrongs
         * do not make a row consistent: the glyph renders identically at both
         * sizes and does not depend on the school laptop's font stack.
         */}
        <BellGlyph size={pill ? 22 : 20} />
        {count > 0 ? (
          /*
           * ⚠️ `aria-hidden`, BECAUSE THE BUTTON'S LABEL ALREADY SAYS IT. Left
           * readable, a screen reader announces "Notifications, 3 unread" and
           * then "3" again.
           *
           * ⚠️ AND IT CAPS AT 9+. The badge is a fixed circle; a three-digit
           * number either overflows it or shrinks the type below legibility, and
           * the difference between 12 and 40 unread changes nothing anybody does.
           */
          <span
            aria-hidden="true"
            className={
              pill
                ? "absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-rose-500 text-[10px] font-extrabold text-white shadow-2xs"
                : "absolute -right-0.5 -top-0.5 inline-flex min-w-4 items-center justify-center rounded-pill px-1 text-[0.625rem] font-bold leading-4 text-white"
            }
            style={pill ? undefined : { background: "var(--color-primary)" }}
          >
            {count > 9 ? "9+" : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={COPY.heading}
          className="absolute right-0 z-50 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-line bg-surface shadow-lg"
        >
          <div className="border-b border-line px-4 py-3 font-dash text-dash-sm font-bold text-ink">
            {COPY.heading}
          </div>
          {items.length === 0 ? (
            <p className="px-4 py-6 font-dash text-dash-xs text-muted">{COPY.empty}</p>
          ) : (
            /* ⚠️ IT SCROLLS INSIDE ITSELF rather than growing past the viewport
               on a 768px-tall laptop, which is the height these screens assume. */
            <ul className="max-h-96 overflow-auto">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="border-b border-line px-4 py-3 font-dash text-dash-xs last:border-b-0"
                >
                  <p className="font-bold text-ink">{item.title}</p>
                  <p className="mt-0.5 text-ink-soft">{item.body}</p>
                  {/* ⚠️ A LABEL, NEVER A PERSON — `authorLabel`'s constraint. A
                      reader learns that Kalqy or their school is speaking, not
                      which operator typed it. */}
                  <p className="mt-1 text-muted">{item.attribution}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
